import { motion, AnimatePresence } from "framer-motion";
import { X, Users, ListTodo, Notebook, NotepadText, Banknote, FileText, BanknoteArrowDown } from "lucide-react";
import { cn } from "../../Lib/Cn";
import { checkUserRights, currentUserHasPermission } from "../../Utils/Utilities";
import { checkIfInqOnlyMode, getAgencyDetails } from "../../Store/Reducers";
import { useSelector } from "react-redux";

export const MobileAddPopup = ({ isOpen, onClose, threadTypes, onOptionClick, currentPage }) => {
  // Check if user can create threads (ThrActions 1 or 3)
  const thrActions = currentUserHasPermission('ThrActions');
  const canCreateThread = thrActions <= 3 && thrActions > 0;

  // Check permissions for other options
  const canAddConnections = currentUserHasPermission('ConnEntry');
  const canAddTasks = currentUserHasPermission('TaskEntry');
  const canAddRecords = currentUserHasPermission('RecordEntry');
  const userRights = checkUserRights();
  const canAddContracts = userRights.addCtrcs;
  const canAddTransactions = userRights.addTxn;
  
  const agencyData = useSelector(getAgencyDetails);
  const isCtrcsEnabled = agencyData?.EnblContracts;
  const isXnsEnabled = agencyData?.EnblXns;
  
  const isInqOnlyMode = useSelector(checkIfInqOnlyMode);

  const handleOptionClick = (option, dialogType = '') => {
    onOptionClick(option, dialogType);
    onClose();
  };

  // Create options array based on permissions
  const getOptionsToShow = () => {
    if (currentPage === '/threads') {
      if (canCreateThread && threadTypes.length > 0) {
        let filteredThreadTypes = isInqOnlyMode 
          ? threadTypes.filter(tt => tt.Sh !== false)
          : threadTypes;
        
        if (filteredThreadTypes.length <= 1) {
          return [];
        }
        
        let formatedThreadTypes = filteredThreadTypes.map(tt => (
          { ...tt, isThread: true }
        ));
        return formatedThreadTypes;
      } else {
        return [];
      }
    }

    const nonThreadOptions = [];

    // Only add Connection if user has permission
    if (canAddConnections) {
      nonThreadOptions.push({ name: 'Contact', Icon: Users, iconColor: "var(--sahi-green)", isThread: false, nameSng: isInqOnlyMode ? "Contact" : "Customer/ Supplier" });
    }

    // Always add Task and Interaction (no permission check)
    nonThreadOptions.push({ name: 'Task', Icon: ListTodo, iconColor: "var(--sahi-green)", isThread: false, nameSng: "Task" });
    nonThreadOptions.push({ name: 'Interaction', Icon: NotepadText, iconColor: "var(--sahi-green)", isThread: false, nameSng: "Interaction" });

    // Add Contract option if user has permission and agency has it enabled
    if (canAddContracts && isCtrcsEnabled) {
      nonThreadOptions.push({ name: 'Contract', Icon: FileText, iconColor: "var(--sahi-green)", isThread: false, nameSng: "Contract" });
    }

    // Add Transaction option if user has permission and agency has it enabled
    if (canAddTransactions && isXnsEnabled) {
      nonThreadOptions.push({ name: 'Transaction', Icon: Banknote, iconColor: "var(--sahi-green)", isThread: false, nameSng: "Transaction" });
    }

    if (canCreateThread) {
      let filteredThreadTypes = isInqOnlyMode 
        ? threadTypes.filter(tt => tt.Sh !== false)
        : threadTypes;
      
      // Show both threadTypes and non-thread options - FIXED: proper array concatenation
      let formatedThreadTypes = filteredThreadTypes.map(tt => (
        { ...tt, isThread: true }
      ))
      return [...formatedThreadTypes, ...nonThreadOptions];
    } else {
      // Show only non-thread options
      return nonThreadOptions;
    }
  };

  const optionsToShow = getOptionsToShow();

  // Container animation: slides up from bottom (very fast)
  const containerVariants = {
    hidden: {
      y: 30,
      opacity: 0,
    },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.1,
        ease: "easeOut",
      },
    },
    exit: {
      y: 30,
      opacity: 0,
      transition: {
        duration: 0.08,
      },
    },
  };

  // Each button animation with reverse index for bottom-to-top
  const getItemVariants = (index, totalItems) => ({
    hidden: {
      y: 25,
      opacity: 0,
      scale: 0.9,
    },
    visible: {
      y: 0,
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.15,
        delay: (totalItems - 1 - index) * 0.03, // Reverse order: last item first
        ease: "easeOut",
      },
    },
  });

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] lg:hidden">
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.12 }}
            onClick={onClose}
          />

          {/* Buttons Container */}
          <motion.div
            className="absolute bottom-0 left-0 right-0 px-3 p-4 pb-24"
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={containerVariants}
          >
            <div className="flex flex-wrap justify-evenly gap-3 max-w-[320px] mx-auto">
              {optionsToShow.map((meta, index, array) => {
                // FIXED: Ensure Icon exists and handle both thread and non-thread options
                const Icon = meta.Icon || Users; // Fallback icon
                const totalItems = array.length;

                return (
                  <motion.button
                    key={meta.name}
                    variants={getItemVariants(index, totalItems)}
                    onClick={() => handleOptionClick(meta, meta.name)}
                    className={cn(
                      "w-[29%] sm:w-[27%] bg-white rounded-lg p-3 flex flex-col items-center justify-center shadow-md text-center",
                      "transition-transform duration-300 hover:scale-[1.02] active:scale-[0.98]"
                    )}
                  >
                    <div
                      className={cn(
                        "w-10 h-10 rounded-lg flex items-center justify-center mb-2",
                        meta.colorClass || ""
                      )}
                      style={{
                        backgroundColor: !meta.isThread
                          ? 'white'
                          : `${meta.textColor}20`
                      }}
                    >
                      <Icon
                        className={cn("w-6 h-6", meta.colorClass || "")}
                        style={!meta.isThread ? { color: meta.iconColor } : {}}
                      />
                    </div>
                    <span className="text-gray-800 font-bold text-sm">
                      {meta.nameSng}
                    </span>
                  </motion.button>
                );
              })}
            </div>

          </motion.div>

          {/* Close Button (Properly Centered) */}
          <motion.div
            className="fixed bottom-6 left-0 right-0 flex justify-center z-[110]"
            initial={{ scale: 0.7, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.7, opacity: 0 }}
            transition={{
              duration: 0.1,
              ease: "easeOut",
            }}
          >
            <button
              onClick={onClose}
              className="w-14 h-14 bg-sahi-primary-color rounded-full flex items-center justify-center shadow-lg"
            >
              <X className="w-7 h-7 text-white" />
            </button>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default MobileAddPopup;